/**
 * One-shot native bootstrap. Call once on app start.
 *
 * On the web this is a no-op. On iOS / Android it:
 *   - hides the splash screen once the bundle is ready
 *   - styles the status bar to match our court-blue theme
 *   - configures the keyboard plugin
 *   - listens for the hardware back button on Android
 */
import { isNativePlatform, getPlatform } from "./platform";

export async function initNative(): Promise<void> {
  if (!isNativePlatform()) return;

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    // Give the JS a moment to mount before fading the splash.
    setTimeout(() => {
      SplashScreen.hide().catch(() => undefined);
    }, 400);
  } catch {
    /* plugin not installed yet — first build of native */
  }

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    if (getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#0a1f3d" });
    }
  } catch {
    /* ignore */
  }

  try {
    const { App } = await import("@capacitor/app");
    // Android hardware back: pop history if there's somewhere to go,
    // otherwise let the OS handle it (exit/minimize).
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp().catch(() => undefined);
      }
    });
  } catch {
    /* ignore */
  }
}
